import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  DollarSign, FileText, CheckCircle, Clock, XCircle, 
  CreditCard, Search, RefreshCw, ExternalLink, TrendingUp
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InvoicesPage = () => {
  const [invoices, setInvoices] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [processingPayment, setProcessingPayment] = useState(null);

  const fetchInvoices = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      params.append('limit', '100');
      
      const response = await axios.get(`${API}/invoices?${params.toString()}`);
      setInvoices(response.data);
    } catch (error) {
      console.error('Error fetching invoices:', error);
      toast.error('Failed to fetch invoices');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/invoices/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  useEffect(() => {
    fetchInvoices();
    fetchStats();
    
    // Check if returning from Stripe
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    const invoiceId = urlParams.get('invoice_id');
    const cancelled = urlParams.get('cancelled');
    
    if (sessionId && invoiceId) {
      // Poll for payment status
      pollPaymentStatus(sessionId, invoiceId);
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (cancelled) {
      toast.info('Payment was cancelled');
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [fetchInvoices]);

  const pollPaymentStatus = async (sessionId, invoiceId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.info('Payment status check timed out. Please refresh.');
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        toast.success('Payment successful! Invoice marked as paid.');
        fetchInvoices();
        fetchStats();
        return;
      } else if (response.data.status === 'expired') {
        toast.error('Payment session expired.');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, invoiceId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
    }
  };

  const initiatePayment = async (invoiceId) => {
    setProcessingPayment(invoiceId);
    try {
      const response = await axios.post(`${API}/invoices/${invoiceId}/payment`, {
        invoice_id: invoiceId,
        origin_url: window.location.origin
      });
      
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Error initiating payment:', error);
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
      setProcessingPayment(null);
    }
  };

  const cancelInvoice = async (invoiceId) => {
    if (!window.confirm('Are you sure you want to cancel this invoice?')) return;
    
    try {
      await axios.put(`${API}/invoices/${invoiceId}/cancel`);
      toast.success('Invoice cancelled');
      fetchInvoices();
      fetchStats();
    } catch (error) {
      toast.error('Failed to cancel invoice');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-500/15 text-amber-500',
      paid: 'bg-emerald-500/15 text-emerald-500',
      cancelled: 'bg-slate-500/15 text-slate-400',
    };
    return styles[status] || styles.pending;
  };

  const StatCard = ({ icon: Icon, title, value, subtitle, color }) => (
    <Card className="glass-card" data-testid={`invoice-stat-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-mono text-muted-foreground uppercase tracking-wider">{title}</p>
            <p className="text-2xl font-black font-heading text-foreground mt-1">{value}</p>
            {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
          </div>
          <div className="p-3 rounded-lg" style={{ backgroundColor: `${color}20` }}>
            <Icon className="w-5 h-5" style={{ color }} />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <Layout>
      <div className="p-6 space-y-6 fade-in" data-testid="invoices-page-container">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-black font-heading tracking-tight text-foreground">Invoices & Payments</h1>
            <p className="text-muted-foreground mt-2">Track freight invoices. Brokers pay carriers directly — Tempus does not hold funds.</p>
          </div>
          <Button onClick={() => { fetchInvoices(); fetchStats(); }} className="glow-button" data-testid="refresh-invoices">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard icon={FileText} title="Total" value={stats.counts?.total ?? stats.total ?? 0} color="#3B82F6" />
            <StatCard icon={Clock} title="Pending" value={stats.counts?.pending ?? stats.pending ?? 0} subtitle={`$${(stats.revenue?.pending_amount ?? stats.total_pending ?? 0).toLocaleString()}`} color="#F59E0B" />
            <StatCard icon={CheckCircle} title="Paid" value={stats.counts?.paid ?? stats.paid ?? 0} subtitle={`$${(stats.revenue?.total_collected ?? stats.total_collected ?? 0).toLocaleString()}`} color="#10B981" />
            <StatCard icon={TrendingUp} title="Collected" value={`$${(stats.revenue?.total_collected ?? stats.total_collected ?? 0).toLocaleString()}`} color="#10B981" />
          </div>
        )}

        {/* Filters */}
        <Card className="glass-card">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4 items-center">
              <div className="flex gap-2">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-4 py-2 rounded-lg bg-background border border-border text-foreground text-sm"
                  data-testid="invoice-status-filter"
                >
                  <option value="">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="paid">Paid</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              <div className="ml-auto text-sm text-muted-foreground">
                Software billing: <span className="font-mono font-bold text-primary">flat monthly</span> · No per-load fee
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Invoice List */}
        <Card className="glass-card">
          <CardHeader>
            <CardTitle className="text-xl font-heading flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-primary" />
              Invoices ({invoices.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-muted-foreground">Loading invoices...</p>
                </div>
              </div>
            ) : invoices.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-50" />
                <h3 className="text-xl font-heading text-foreground mb-2">No Invoices Yet</h3>
                <p className="text-sm text-muted-foreground">Invoices are automatically created when loads are completed</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-[60vh] overflow-y-auto">
                {invoices.map((invoice) => (
                  <div
                    key={invoice.id}
                    className="flex items-center gap-4 p-4 rounded-lg bg-background/50 hover:bg-accent/50 transition-colors"
                    data-testid={`invoice-row-${invoice.id}`}
                  >
                    <div className="p-2 rounded-lg bg-primary/15">
                      <FileText className="w-5 h-5 text-primary" />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-sm font-semibold text-foreground truncate">{invoice.broker_name || invoice.driver_name || 'Broker'}</p>
                        <span className={`px-2 py-0.5 text-xs font-semibold rounded-md ${getStatusBadge(invoice.status)}`}>
                          {invoice.status}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span>Invoice: <span className="font-mono">{invoice.invoice_number || invoice.id.substring(0, 8)}</span></span>
                        {invoice.due_date && <span>Due: <span className="font-mono">{invoice.due_date}</span></span>}
                        <span className="font-mono">ID: {invoice.id.substring(0, 8)}</span>
                      </div>
                    </div>

                    <div className="text-right">
                      <p className="text-2xl font-black font-heading text-foreground">${(invoice.total ?? invoice.fee_amount ?? invoice.load_rate ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                      <p className="text-xs text-muted-foreground">payable to carrier</p>
                    </div>

                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => window.open(`/invoice/${invoice.id}`, '_blank')}
                        data-testid={`view-invoice-${invoice.id}`}
                      >
                        <FileText className="w-4 h-4 mr-1" />
                        View
                      </Button>
                      {invoice.status === 'paid' && (
                        <span className="text-xs text-emerald-500 flex items-center gap-1">
                          <CheckCircle className="w-4 h-4" />
                          Paid
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card className="glass-card bg-blue-500/10 border-blue-500/20">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <DollarSign className="w-5 h-5 text-blue-500 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-blue-500">How Invoicing Works (SaaS Model)</p>
                <p className="text-xs text-blue-500/80 mt-1">
                  1. When a load is marked as completed, a freight invoice is auto-generated for the carrier to bill the broker.
                  2. Brokers pay carriers DIRECTLY (ACH/check/wire) — Tempus does not hold or process freight payments.
                  3. Tempus charges its carrier customers 6% of gross monthly freight revenue (usage-based), billed separately via Stripe.
                  4. Brokers can record payment via the public invoice link to keep an audit trail.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default InvoicesPage;
